import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { GenericStep } from '@/components/FullWidthTemplate/GenericStep';
import AlterDomainBR from '@/media/onboarding/temporary-domain-br.png';
import AlterDomainES from '@/media/onboarding/temporary-domain-es.webp';
import NavBar from '@/media/onboarding/navbar.svg';
import { onboardingAnalytics } from '@/analytics/onboarding';
import {
  IconCheckBg, IconGlobeCursor, SelectField, TextField,
  Toggle,
} from 'gatorcomponents';
import * as Styles from './ChooseDomainStep.styles';
import { ChooseDomainStepProps } from './ChooseDomainStep.types';
import { isBRServer, domain as isDomain } from '@/utils/Validators/validation';

const ChooseDomainStep = ({
  locales,
  currentFlow,
  changeDomainLoading,
  buttonsProps,
  showAutoCompleteDomain,
  autoCompleteOptions,
  autoCompleteDomain,
  setShowAutoCompleteDomain,
  domainMessageError,
  domainMessageErrorBr,
  insideWizard,
  userDomainsList,
  selectedDomain,
  setSelectedDomain,
  handleContinueInsideWizard,
  loadingSites,
  loadingDomainValidation,
  loadingVerifyIfSiteExist,
  loadingUninstall,
  usingTempDomain,
  loadingTempDomainCreation,
  resetHandleDomainAvailability,
  allowTempDomainCreation,
  FLOWS,
}) => {
  useEffect(() => {
    resetHandleDomainAvailability && resetHandleDomainAvailability();
  }, [resetHandleDomainAvailability]);

  const [isValidDomain, setIsValidDomain] = useState(false);
  const [toggleInputType, setToggleInputType] = useState(false);
  const [selectedObject, setSelectedObject] = useState(null);

  const inputWithAutoComplete = useRef(null);
  const togglowRow = useRef(null);

  const temporarySubdomain = '';

  const defaultErrors = useMemo(() => ({
    invalidDomain: false,
  }), []);

  const [customTemporaryDomain, setCustomTemporaryDomain] = useState(temporarySubdomain);
  const [inputError, setInputError] = useState(defaultErrors);

  const cleanupFunction = useCallback(() => {
    setCustomTemporaryDomain(temporarySubdomain);
    setSelectedDomain(null);
    setSelectedObject(null);
    setCustomTemporaryDomain(null);
    setInputError(defaultErrors);
  }, [defaultErrors, temporarySubdomain, setSelectedDomain]);

  useEffect(() => () => {
    cleanupFunction();
  }, [cleanupFunction]);

  const hasSomeError = Object.values(inputError).some(error => error);

  const setErrors = useCallback((domain) => {
    const domainInUse = domain || customTemporaryDomain;
    const domainIsValid = isDomain(domainInUse);

    setInputError({
      invalidDomain: domainInUse ? !domainIsValid : false,
    });

    setIsValidDomain(domainIsValid);
  }, [
    customTemporaryDomain,
    setIsValidDomain,
  ]);

  const handleOutsideInputClick = useCallback((event) => {
    if (
      (inputWithAutoComplete.current && !inputWithAutoComplete.current.contains(event.target))
      && (togglowRow.current && !togglowRow.current.contains(event.target))
    ) {
      setErrors();
      setShowAutoCompleteDomain(false);
    }
  }, [setErrors, setShowAutoCompleteDomain]);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideInputClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideInputClick);
    };
  }, [handleOutsideInputClick]);

  const onChangeDomain = (e) => {
    setCustomTemporaryDomain(e.target.value.toLowerCase());
    autoCompleteDomain && autoCompleteDomain({ domain: e.target.value.toLowerCase() });
  };

  const mountInputErrorMessage = useCallback(() => {
    if (inputError.invalidDomain) {
      return isBRServer ? domainMessageErrorBr : domainMessageError;
    }

    if (inputError.hasSpecialChars) {
      return locales.inputErrors.hasSpecialChars;
    }
    return '';
  }, [inputError, locales.inputErrors, domainMessageError, domainMessageErrorBr]);

  const inputErrorMessage = mountInputErrorMessage();

  const handleOnBlur = () => {
    !insideWizard && setErrors();
    setSelectedDomain && setSelectedDomain(customTemporaryDomain);
  };

  const handleOnFocus = () => {
    if (hasSomeError) {
      setInputError(defaultErrors);
    }
  };

  const handleClickContinue = useCallback(() => {
    if (insideWizard) {
      handleContinueInsideWizard && handleContinueInsideWizard({
        ...selectedObject,
        selectedDomain,
      });
    }

    if (!insideWizard) {
      const newDomain = customTemporaryDomain;
      const needRequestChangeDomain = customTemporaryDomain !== temporarySubdomain;

      const handleContinue = buttonsProps && buttonsProps.rightButton && buttonsProps.rightButton && buttonsProps.rightButton[1].onClick;
      handleContinue && handleContinue({ newDomain, needRequestChangeDomain });
    }
  }, [
    customTemporaryDomain,
    temporarySubdomain,
    buttonsProps,
    insideWizard,
    handleContinueInsideWizard,
    selectedDomain,
    selectedObject,
  ]);

  const stepProps = useCallback(() => {
    const newRightButton = [...buttonsProps.rightButton];

    const isLoading = changeDomainLoading
      || loadingSites
      || loadingVerifyIfSiteExist
      || loadingUninstall
      || loadingDomainValidation
      || loadingTempDomainCreation;


    newRightButton[1] = {
      ...buttonsProps.rightButton[1],
      disabled: (
        (
          (
            hasSomeError
            || (!customTemporaryDomain && !selectedDomain)
          )
          && (!usingTempDomain || (usingTempDomain && loadingTempDomainCreation))
        )
        || isLoading
      ),
      loading: isLoading,
      onClick: handleClickContinue,
    };

    if (currentFlow === FLOWS.SITE_BUILDER || (!usingTempDomain && allowTempDomainCreation)) {
      newRightButton.splice(0, 1);
    }

    const customButtonsProps = {
      ...buttonsProps,
      rightButton: newRightButton,
    };


    return {
      icon: <IconGlobeCursor color="primaryMedium" />,
      title: usingTempDomain ? locales.usingTempDomain.title : locales.title,
      description: usingTempDomain ? locales.usingTempDomain.description : locales.description,
      testId: 'choose-domain-step',
      imageSrc: isBRServer ? AlterDomainBR : AlterDomainES,
      buttonsProps: customButtonsProps,
    };
  }, [
    buttonsProps,
    hasSomeError,
    locales,
    changeDomainLoading,
    handleClickContinue,
    customTemporaryDomain,
    selectedDomain,
    loadingSites,
    loadingVerifyIfSiteExist,
    loadingUninstall,
    loadingDomainValidation,
    currentFlow,
    usingTempDomain,
    loadingTempDomainCreation,
    allowTempDomainCreation,
    FLOWS,
  ]);

  const handleGetAutocomplet = (domain) => {
    setCustomTemporaryDomain(domain);
    setSelectedDomain && setSelectedDomain(domain);
    autoCompleteDomain({ domain });
    setShowAutoCompleteDomain(false);
    setErrors(domain);
  };

  const preventDefault = (e) => {
    e.preventDefault();
  };

  const handleSelectDomain = useCallback((domainOption) => {
    setSelectedDomain && setSelectedDomain(domainOption.label);
    setSelectedObject(domainOption);
  }, [
    setSelectedDomain,
  ]);

  const handleToggleInputType = useCallback(() => {
    setSelectedDomain(null);
    setSelectedObject(null);
    setCustomTemporaryDomain(null);
    setInputError(defaultErrors);
    setToggleInputType(!toggleInputType);
    onboardingAnalytics.chooseDomain.selectAnotherDomain();
  }, [
    toggleInputType,
    defaultErrors,
    setToggleInputType,
    setSelectedDomain,
    setSelectedObject,
  ]);

  const shouldRenderOkAlert = !!customTemporaryDomain && !hasSomeError && isValidDomain;

  const renderInputWithAutocomplete = () => (
    <>
      <Styles.TextFieldWrapper $beforeContent="" $selectDomain>
        <TextField
          label={locales.placeholder}
          testId="input-domain"
          value={customTemporaryDomain}
          onBlur={handleOnBlur}
          onChange={onChangeDomain}
          onFocusInput={handleOnFocus}
          status={hasSomeError ? 'error' : 'normal'}
          helpText={inputErrorMessage}
        />
      </Styles.TextFieldWrapper>

      {showAutoCompleteDomain && (
        <Styles.AutoComplete>
          {autoCompleteOptions.map(item => (
            <Styles.AutoCompleteItem key={item.domain} role="button" onClick={() => handleGetAutocomplet(item.domain)}>{item.domain}</Styles.AutoCompleteItem>
          ))}
        </Styles.AutoComplete>
      )}
    </>
  );

  return (
    <GenericStep {...stepProps()}>
      {usingTempDomain && (
        <>
          <Styles.TempDomainInfo>
            {locales.usingTempDomain.tempDomainInfo[0]}
          </Styles.TempDomainInfo>

          <Styles.TempDomainInfo $moreTopSpace>
            {locales.usingTempDomain.tempDomainInfo[1]}
          </Styles.TempDomainInfo>

          <Styles.LinkWrapper>
            {locales.usingTempDomain.understandTheDifference}
          </Styles.LinkWrapper>
        </>
      )}


      {!usingTempDomain && (
        <>
          <Styles.NavWrapper $withoutOverflow={showAutoCompleteDomain || insideWizard}>
            <Styles.NavbarContainer>
              <Styles.NavbarImage src={NavBar} />
            </Styles.NavbarContainer>

            <Styles.InputContainer
              $hasError={hasSomeError}
              autoComplete="off"
              onSubmit={preventDefault}
            >
              {insideWizard && (
                <Styles.InsideWizardContainer>
                  <Styles.InputWrapper ref={inputWithAutoComplete} $selectDomain>
                    {toggleInputType ? (
                      renderInputWithAutocomplete()
                    ) : (
                      <Styles.SelectWrapper>
                        <SelectField
                          placeholder={locales.selectDomainLabel}
                          options={userDomainsList}
                          value={selectedDomain}
                          onClickSelect={handleSelectDomain}
                          withScroll
                          filterable
                        />
                      </Styles.SelectWrapper>
                    )}
                  </Styles.InputWrapper>

                  <Styles.ToggleRow ref={togglowRow}>
                    {locales.wantToUseAnotherDomain}
                    <Toggle
                      testId="toggleInputType"
                      checked={toggleInputType}
                      onMark={handleToggleInputType}
                    />
                  </Styles.ToggleRow>
                </Styles.InsideWizardContainer>
              )}

              {!insideWizard && (
                <Styles.InputWrapper ref={inputWithAutoComplete} $selectDomain>
                  {renderInputWithAutocomplete()}
                </Styles.InputWrapper>
              )}
            </Styles.InputContainer>

            {(!insideWizard) && (
              <Styles.DomainOkAlert $display={shouldRenderOkAlert} data-testid="domain-validation-success-alert">
                <IconCheckBg secondaryColor="activeLight" color="primaryExtraDark" />
                <Styles.DomainOkAlertRow>
                  <Styles.DomainOkAlertTitle>{locales.alertTitle}</Styles.DomainOkAlertTitle>
                  <Styles.DomainOkAlertDescription>
                    {locales.alertDescription}
                  </Styles.DomainOkAlertDescription>
                </Styles.DomainOkAlertRow>
              </Styles.DomainOkAlert>
            )}
          </Styles.NavWrapper>
        </>
      )}
    </GenericStep>
  );
};

ChooseDomainStep.propTypes = ChooseDomainStepProps;

export default ChooseDomainStep;
